import IntradayScannerTable from "../components/IntradayScannerTable";
import useIntradayScanner from "../hooks/useIntradayScanner";
import Box from "@mui/material/Box";
import { Button, Typography, Alert, Stack, TextField, InputAdornment } from "@mui/material";
import { useContext, useEffect, useState, useMemo } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { useLivePrices } from "../hooks/useLivePrices";
import { getIntradaySettings, updateIntradaySettings } from "../services/scannerService";
import SearchIcon from "@mui/icons-material/Search";

export default function IntradayStocks() {
  const [selectedDate, setSelectedDate] = useState("");
  
  const {
    intradayResults,
    triggerScan,
    orhTrigger,
    vwapTrigger,
    strategyTrigger,
    scanning,
    loadingAction,
    refreshResults
  } = useIntradayScanner(selectedDate);

  const { prices, getLivePrice } = useLivePrices();
  const [searchTerm, setSearchTerm] = useState("");

  const authContext = useContext(AuthContext);
  const isAdmin = authContext?.isAdmin || false;

  useEffect(() => {
    if (intradayResults.length > 0) {
      const tokens = intradayResults
        .map((item: any) => Number(item.instrument_token))
        .filter(Boolean);
      
      if (tokens.length > 0) {
        getLivePrice(tokens);
      }
    }
  }, [intradayResults, getLivePrice]);

  const filteredResults = useMemo(() => {
    if (!searchTerm) return intradayResults;
    return intradayResults.filter((stock: any) =>
      stock.tradingsymbol.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [intradayResults, searchTerm]);

  return ( 
    <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          borderBottom: "1px solid #e0dcdc",
          marginBottom: "15px",
          gap: "10px",
          paddingBottom: "10px"
        }}
      >
        <h4 className="text-2xl font-bold">
          Intraday Stocks
        </h4>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            type="date"
            size="small"
            variant="outlined"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            sx={{ 
              width: { xs: "100%", sm: 160 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "#f8fafc"
              }
            }}
          />
          <TextField
            size="small"
            placeholder="Search Symbol..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              width: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "#f8fafc"
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            size="small"
            onClick={() => refreshResults()}
            variant="outlined"
            sx={{ borderRadius: "20px" }}
          >
            Refresh
          </Button>
          {isAdmin && (
            <>
              <Button
                size="small"
                onClick={() => triggerScan()}
                disabled={scanning || !!loadingAction}
                variant="contained"
                color="secondary"
                sx={{ borderRadius: "20px" }}
              >
                {scanning ? "Scanning..." : "Sync Stocks"}
              </Button>
              <Button
                size="small"
                onClick={() => orhTrigger()}
                disabled={scanning || !!loadingAction}
                variant="contained"
                color="info"
                sx={{ borderRadius: "20px" }}
              >
                {loadingAction === "orh" ? "Calculating..." : "ORH/ORL"}
              </Button>
              <Button
                size="small"
                onClick={() => vwapTrigger()}
                disabled={scanning || !!loadingAction}
                variant="contained"
                color="warning"
                sx={{ borderRadius: "20px" }}
              >
                {loadingAction === "vwap" ? "Updating..." : "1 min VWAP"}
              </Button>
              <Button
                size="small"
                onClick={() => strategyTrigger()}
                disabled={scanning || !!loadingAction}
                variant="contained"
                color="error"
                sx={{ borderRadius: "20px" }}
              >
                {loadingAction === "strategy" ? "Running..." : "Signals"}
              </Button>
            </>
          )}
        </div>
      </div>

      <Alert severity="info" sx={{ mb: 3 }}>
        Gap-up/down stocks confirmation (9:00 - 9:08 AM). Use <strong>OR + VWAP</strong> for final entry confirmation after 9:15 AM.
      </Alert>

      <IntradayScannerTable
        results={filteredResults}
        isLoading={scanning}
        prices={prices as any}
      />
    </Box>
  );
}
